import { PowerpointExportDto } from '@ecosystem/export-api';
import { ExportFormat } from '@ecosystem/newron-client-lib';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { useAnalyticsContext } from '../analytics/analytics.provider';
import { getComponentSettingsTrackingNames } from '../analytics/trackPageElementViewed.hook';
import { addChartTypeToEventParams, getMetadataTrackingParams } from '../analytics/utils';
import { pageClient } from '../client';
import { ContentElement } from '../content/element';
import { ContentElementHasData } from '../content/types';
import { ContextManager } from '../context/manager';
import { useMobile } from '../context/mobile/mobile.provider';
import { getParamsForContext, getPropositionFromContext } from '../context/utils';
import { UiDataParams } from '../data';
import { DataProviderElement, useDataState } from '../data/provider';
import { DataStore } from '../data/store';
import GenericWarnings from '../design/warning/GenericWarnings';
import { MobileOverflowMenu } from '../elements/mobileMenu/mobileOverflowMenu';
import { MultiKpiCollectionButton } from '../elements/multiKpi/MultiKpiCollectionButton';
import { MultiKpiCollectionButtonProps } from '../elements/multiKpi/types';
import { componentToPptData } from '../export';
import { ExportButton, ExportButtonProps } from '../export/export.button';
import { ExportHideout } from '../export/ExportHideout';
import { ComponentHasData, useCorePageState } from '../page/provider';
import { PageSettingsManager } from '../page/settings';
import { ComponentHeaderActionType } from '../page/types';
import { useIsMounted } from '../tools';
import { hasFeatureTag } from '../tools/featureToggle/utils';
import { makeUrl } from '../tools/strings';
import { BasicTrackingFn, Component, Content, EventName, EventParams, Params } from '../types';

import { componentActions } from './component.actions';
import { ComponentCard } from './component.card';
import { componentFooter } from './component.footer';
import LocalErrorBoundary from './LocalErrorBoundary';
import { mapKeyIndicatorsForExport } from '../export/utils/pptUtils';

type Props = {
  component: Component;
};

function ComponentElement({ component }: Props) {
  const [componentSettings, setComponentSettings] = useState<Params>();
  const [componentContents, setComponentContents] = useState<Content[]>();
  const [componentContextParams, setComponentContextParams] = useState<Params>();
  const [componentPageParams, setComponentPageParams] = useState<Params>();
  const { data } = useDataState();
  const cardElementRefs = useRef<HTMLDivElement[]>([]);

  const contentsPending = useRef<boolean>(false);
  const isMounted = useIsMounted();
  const shouldUseDataLoading = typeof window !== 'undefined' && window.location.pathname.startsWith('/mo');
  const {
    someComponentsHaveData,
    setComponentHasData,
    componentHasDataMap,
    endpoints,
    contextParams,
    pageParams,
    dslLoading,
    dataLoading,
    contextLoading,
    page,
    data: dataInfo,
    context,
    collection: collectionInfo,
    updatePageSettings,
    dsl,
    resetPageWarnings,
  } = useCorePageState();
  const { trackPageEvent } = useAnalyticsContext();
  const [contentElementHasDataMap, setContentElementHasDataMap] = useState<Record<string, ContentElementHasData>>({});
  const { isMobile } = useMobile();

  const atLeastOneContentElementHasData = () => {
    return Object.keys(contentElementHasDataMap).some(
      key => contentElementHasDataMap[key] === true || contentElementHasDataMap[key] === 'isWarning'
    );
  };
  const atLeastOneContentElementIsLoading = () => {
    return Object.keys(contentElementHasDataMap).some(key => contentElementHasDataMap[key] === 'isLoading');
  };

  const allComponentsHaveWarning = () => {
    return Object.keys(contentElementHasDataMap).every(key => contentElementHasDataMap[key] === 'isWarning');
  };

  useEffect(() => {
    if (componentContents?.length === 0) {
      setComponentHasData(component.name, false);
      return;
    }
    if (atLeastOneContentElementHasData()) {
      setComponentHasData(component.name, true);
      return;
    }
    if (atLeastOneContentElementIsLoading()) {
      setComponentHasData(component.name, 'isLoading');
      return;
    }
    setComponentHasData(component.name, false);
  }, [contentElementHasDataMap, componentContents]);

  const componentHasData = (): ComponentHasData => {
    return componentHasDataMap[component.name];
  };

  const {
    name,
    dataOptions,
    settings: { values, dataSettings, metadata },
    contents,
    heading,
    exportOptions,
    collectionOptions,
  } = component;

  useEffect(() => {
    setComponentContents(component.contents.fixed || []);
  }, [component.contents.fixed]);

  const handleUpdateSettings = (updates: Params, shouldUpdateDsl: boolean = false) => {
    if (Object.keys(updates).includes('moKeyBrands')) {
      //only for MO focused Brands filter change, reset page warnings
      resetPageWarnings();
    }
    const newSettings = {
      ...{},
      ...componentSettings,
      ...updates,
    };
    setComponentSettings(newSettings);
    const fetchContentsCallback = () => {
      if (hasFeatureTag('m2-detailed-channels-hide-others') && shouldUpdateDsl) {
        updatePageSettings(updates);
        return;
      }
      if (contents.endpoint && !contents.fixed) {
        fetchContents(newSettings);
      }
    };
    PageSettingsManager.getInstance().updateComponentSettings(name, newSettings, fetchContentsCallback);
  };

  const fetchContents = async (settings: Params) => {
    const { endpoint } = contents;
    if (!endpoint) {
      return;
    }

    if (contentsPending.current) {
      return;
    }

    try {
      contentsPending.current = true;
      const contents = await pageClient.getComponentContents(makeUrl(endpoints.page, endpoint), {
        ...{},
        ...contextParams,
        ...settings,
      });
      if (isMounted()) {
        setComponentContents(contents);
      }
    } catch (err) {
      throw err;
    } finally {
      contentsPending.current = false;
    }
  };

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    if (dslLoading) {
      return;
    }
    const cached = PageSettingsManager.getInstance().getComponentSettings(name) || {};
    handleUpdateSettings({
      ...cached,
      ...values,
    });
  }, [name, values, dslLoading]);

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    if (dslLoading) {
      return;
    }
    setComponentContextParams(contextParams);
  }, [contextParams]);

  useEffect(() => {
    if (dslLoading) {
      return;
    }
    setComponentPageParams(pageParams);
  }, [pageParams]);

  const paramsForData: Params = {
    ...{},
    ...componentContextParams,
    ...componentPageParams,
  };

  const contextParamsData = ContextManager.getInstance().get();
  const proposition = getPropositionFromContext(contextParamsData);
  const paramsForContext: Params = getParamsForContext(contextParamsData);

  if (componentSettings && componentContextParams) {
    dataSettings.forEach(name => {
      if (componentSettings[name]) {
        paramsForData[name] = componentSettings[name];
      }
    });
    Object.keys(UiDataParams).forEach(name => {
      if (componentSettings[name]) {
        paramsForData[name] = componentSettings[name];
      }
    });
  }

  const trackContentEvents: BasicTrackingFn = useCallback(
    (eventName: EventName, eventParams: EventParams) => {
      if (eventName === EventName.ChartInteractions) {
        eventParams = addChartTypeToEventParams(eventParams, componentSettings);
      }
      if (eventName === EventName.PageElementViewed) {
        eventParams = getComponentSettingsTrackingNames(componentSettings, heading.title, eventParams);
      }
      if (eventName === EventName.DataExported) {
        eventParams = getComponentSettingsTrackingNames(componentSettings, heading.title, eventParams);
      }
      if (eventName === EventName.PageElementClicked) {
        eventParams = getMetadataTrackingParams(metadata, eventParams);
      }
      trackPageEvent(eventName, eventParams);
    },
    [trackPageEvent, componentSettings]
  );

  const setContentElementHasData = (contentElementId, hasData) => {
    setContentElementHasDataMap(prevState => {
      return {
        ...prevState,
        [contentElementId]: hasData,
      };
    });
  };

  const hideEmptyState = () => {
    const emptyState = 'hideEmptyState';
    return !componentSettings[emptyState]?.[0];
  };

  const areActionsEnabled = () => {
    return !allComponentsHaveWarning() && componentHasData();
  };
  const getExportData = async (_exportFormat: ExportFormat, data: DataStore): Promise<PowerpointExportDto> => {
    const allColumns = data.getAllColumns();
    const exportContainer = document.querySelector(`#${name}_export_content`);
    let slidesContainerSelector = '';
    if (exportContainer.classList.contains('inline-flex')) {
      slidesContainerSelector = `#${name}_export_content`;
    } else {
      slidesContainerSelector = `.${name}-export-slide`;
    }
    let exportSlides: Element[] = Array.from(document.querySelectorAll(slidesContainerSelector));

    if (name === 'overviewKeyIndicators') {
      exportSlides = mapKeyIndicatorsForExport(exportSlides);
    }

    exportSlides.forEach((slide, idx) => (cardElementRefs.current[idx] = slide as HTMLDivElement));
    return await componentToPptData(
      dsl,
      exportOptions,
      component,
      cardElementRefs.current,
      componentSettings,
      allColumns
    );
  };

  if (!componentSettings || !componentContextParams) {
    return null;
  }

  const multiKpiCollectionButtonProps: MultiKpiCollectionButtonProps = {
    collectionOptions,
    collectionInfo,
    proposition,
    paramsForData,
    paramsForContext,
    page,
    context,
    dataInfo,
    componentSettings,
    isDisabled: shouldUseDataLoading ? dataLoading : atLeastOneContentElementIsLoading(),
  };

  const exportButtonProps: ExportButtonProps = {
    trackingSourceData: data,
    componentSettings,
    exportFormats: [ExportFormat.Pptx],
    getExportData: getExportData,
    disabled: atLeastOneContentElementIsLoading(),
    exportContentRef: cardElementRefs,
    hasExternalExportContent: true,
    trackEvent: trackContentEvents,
    componentMetadata: metadata,
  };

  const renderActionsContainer = () => {
    return (
      <>
        <MultiKpiCollectionButton {...multiKpiCollectionButtonProps} actionType={ComponentHeaderActionType.Button} />
        {exportOptions && <ExportButton {...exportButtonProps} actionType={ComponentHeaderActionType.Button} />}
      </>
    );
  };

  const renderMobileActionsContainer = () => {
    if (page.name !== 'overview') return;
    return (
      <MobileOverflowMenu
        isLoading={shouldUseDataLoading ? dataLoading : atLeastOneContentElementIsLoading()}
        isAiEnabled={false}
        canExportDataSet={!!exportOptions}
        canEditKpis={false}
        canToggleDataViz={false}
        multiKpiCollectionButtonProps={multiKpiCollectionButtonProps}
        exportButtonProps={exportButtonProps}
      />
    );
  };

  const ComponentActionsContainer = heading !== undefined ? componentActions(name) : undefined;
  return (
    <LocalErrorBoundary>
      <DataProviderElement dataOptions={dataOptions} paramsForData={paramsForData} urlRoot={endpoints.data}>
        {someComponentsHaveData && !!data && <GenericWarnings warnings={data.getGenericWarnings()} />}
        {exportOptions && (
          <ExportHideout>
            <div id={`${name}_export_content`} ref={element => (cardElementRefs.current = [element])} />
          </ExportHideout>
        )}
        <ComponentCard
          component={component}
          showNoDataInfo={hideEmptyState() && componentHasData() === false}
          settings={componentSettings}
          updateComponentSettings={handleUpdateSettings}
          trackEvent={trackContentEvents}
          metadata={metadata}
          paramsForData={paramsForData}
        >
          {ComponentActionsContainer && areActionsEnabled() && (
            <ComponentActionsContainer>
              {!isMobile ? renderActionsContainer() : renderMobileActionsContainer()}
            </ComponentActionsContainer>
          )}
          {componentContents?.map((content, idx) => (
            <ContentElement
              key={`${name}_content_${idx}`}
              contentElementId={`${name}_content_${idx}`}
              hideElement={componentHasData() === false}
              setContentElementHasData={setContentElementHasData}
              content={content}
              updateComponentSettings={handleUpdateSettings}
              paramsForData={paramsForData}
              dslOrContextLoading={dslLoading || contextLoading}
              dataUrlRoot={endpoints.data}
              componentActions={ComponentActionsContainer}
              componentFooter={componentFooter(name)}
              componentSettings={componentSettings}
              trackEvent={trackContentEvents}
              componentMetadata={metadata}
              componentName={name}
              componentHeadingTitle={component?.heading?.title}
            />
          ))}
        </ComponentCard>
      </DataProviderElement>
    </LocalErrorBoundary>
  );
}

export { ComponentElement };
